// src/components/HamburgerMenu.jsx
// Menu hamburguesa flotante que agrupa los FABs antes sueltos:
// - Notificaciones (badge + dropdown)
// - Tema (light/dark toggle)
// - Soporte (WhatsApp Bellason)
// - Instalar app
// - Cerrar sesión
//
// Se muestra en la esquina superior derecha. Al tocarlo se despliega un
// panel con todos los items. Solo aparece cuando hay sesión.
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import { useInstallPWA } from '../hooks/useInstallPWA'
import { playNotificationSound } from '../lib/notificationSound'

const SUPPORT_URL = 'mailto:soporte@bellason.mx?subject=Soporte%20Bellason&body=Hola%20Bellason%2C%20necesito%20soporte%3A%0A%0A'
const STORAGE_KEY_LAST_SEEN = 'bellason_notifs_last_seen'

export default function HamburgerMenu() {
  const { user, role } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { canInstall, isInstalled, install, isIOS } = useInstallPWA()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [section, setSection] = useState(null) // null | 'notifs'
  const [notifs, setNotifs] = useState([])
  const [unread, setUnread] = useState(0)
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [installHelp, setInstallHelp] = useState(null) // 'ios' | 'desktop' | null
  const panelRef = useRef(null)

  // Carga inicial + realtime desde la tabla notifications. Antes mantenia un
  // array solo-en-memoria que se perdia al recargar y NO incluia broadcasts
  // del admin (solo eventos realtime mientras la app estaba abierta).
  // Ahora la tabla notifications es la fuente de verdad:
  //   - Triggers en DB insertan automaticamente (new_offer, offer_accepted,
  //     service_completed, new_request)
  //   - send-broadcast inserta una fila por cada user matched
  //   - El panel hace SELECT inicial + se suscribe a INSERTs realtime
  useEffect(() => {
    if (!user) return () => {}
    let unmounted = false

    async function load() {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const { data } = await supabase
        .from('notifications')
        .select('id, kind, title, body, url, read_at, created_at')
        .eq('user_id', user.id)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(50)
      if (unmounted) return
      const list = (data || []).map(n => ({
        id: n.id,
        kind: n.kind,
        title: n.title,
        body: n.body,
        url: n.url,
        ts: new Date(n.created_at).getTime(),
        read: !!n.read_at,
      }))
      setNotifs(list)
      setUnread(list.filter(n => !n.read).length)
    }
    load()

    const ch = supabase
      .channel(`hm-notifs-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        const n = payload.new
        let isNew = false
        setNotifs((prev) => {
          if (prev.some(x => x.id === n.id)) return prev
          isNew = true
          return [{
            id: n.id, kind: n.kind, title: n.title, body: n.body, url: n.url,
            ts: new Date(n.created_at).getTime(), read: false,
          }, ...prev].slice(0, 50)
        })
        if (isNew) {
          setUnread((u) => u + 1)
          // Sonar custom MP3 en TODA notificacion nueva — broadcast, oferta,
          // acceptacion, nuevo pedido, servicio liberado. Audio singleton se
          // resetea si suena muy seguido (no overlap). Si el browser bloquea
          // por autoplay policy (no hubo touch aun) falla silenciosamente.
          const urgent = n.kind === 'new_offer'
                      || n.kind === 'offer_accepted'
                      || n.kind === 'new_request'
          try { playNotificationSound(urgent) } catch { /* ok */ }
        }
      })
      .subscribe()

    return () => {
      unmounted = true
      supabase.removeChannel(ch)
    }
  }, [user])

  function openMenu() {
    setOpen(true)
    setSection(null)
  }
  function close() {
    setOpen(false)
    setSection(null)
  }
  function showNotifs() {
    setSection('notifs')
    setUnread(0)
    try { localStorage.setItem(STORAGE_KEY_LAST_SEEN, String(Date.now())) } catch { /* ok */ }
    // Marcar todas como leidas server-side (UPDATE read_at = now())
    if (user?.id) {
      supabase.from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .is('read_at', null)
        .then(() => {
          setNotifs(prev => prev.map(n => ({ ...n, read: true })))
        })
    }
  }
  function go(url) {
    close()
    if (url) navigate(url)
  }

  async function doLogout() {
    await supabase.auth.signOut()
    close()
    navigate('/login', { replace: true })
  }

  if (!user) return null

  return (
    <>
      {/* Trigger flotante */}
      <button type="button"
        onClick={openMenu}
        aria-label="Menú"
        title="Menú"
        style={{
          position: 'fixed', top: 14, right: 14,
          width: 42, height: 42, borderRadius: '50%',
          background: 'var(--bln-bg-elevated)',
          color: 'var(--bln-text)',
          border: '1px solid var(--bln-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 65, padding: 0,
          boxShadow: 'var(--bln-shadow-sm)',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -3,
            background: 'var(--bln-danger)', color: 'var(--bln-text)',
            fontSize: 10, fontWeight: 700,
            minWidth: 18, height: 18, borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid var(--bln-bg)',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'var(--bln-bg-overlay)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
            padding: '60px 14px',
          }}
        >
          <div
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bln-bg-elevated)',
              border: '1px solid var(--bln-border)',
              borderRadius: 14,
              width: '100%', maxWidth: 320,
              maxHeight: '78vh', overflowY: 'auto',
              boxShadow: 'var(--bln-shadow-lg)',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '14px 16px', borderBottom: '1px solid var(--bln-border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ color: 'var(--bln-text)', fontSize: 14, fontWeight: 700 }}>
                {section === 'notifs' ? 'Notificaciones' : 'Menú'}
              </span>
              <button onClick={close} style={{
                background: 'none', border: 'none', color: 'var(--bln-text-muted)',
                cursor: 'pointer', fontSize: 22, padding: 0, lineHeight: 1,
              }}>✕</button>
            </div>

            {section === 'notifs' ? (
              <NotifList notifs={notifs} onGo={go} />
            ) : (
              <div>
                <Item icon="🔔" label="Notificaciones" badge={unread} onClick={showNotifs} />
                <Item
                  icon={theme === 'dark' ? '☀️' : '🌙'}
                  label={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                  onClick={toggleTheme}
                />
                <Item
                  icon="📲"
                  label={isInstalled ? 'Ya instalada' : (canInstall || isIOS ? 'Instalar app' : 'Cómo instalar')}
                  disabled={isInstalled}
                  onClick={() => {
                    if (isInstalled) return
                    if (canInstall) { install(); close(); return }
                    // alert() en iOS PWA standalone se ignora o bloquea el
                    // JS thread. Usamos un modal interno.
                    setInstallHelp(isIOS ? 'ios' : 'desktop')
                    close()
                  }}
                />
                <Item icon="✉️" label="Soporte" onClick={() => { window.location.href = SUPPORT_URL; close() }} />
                <Item icon="↪︎" label="Cerrar sesión" danger onClick={() => setConfirmLogout(true)} />
              </div>
            )}
          </div>
        </div>
      )}

      {installHelp && (
        <div
          onClick={() => setInstallHelp(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 110,
            background: 'var(--bln-bg-overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--bln-bg-elevated)', borderRadius: 16,
            padding: '20px 22px', maxWidth: 360, width: '100%',
            border: '1px solid var(--bln-border)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--bln-text)', marginBottom: 8 }}>
              Cómo instalar Bellason
            </div>
            <div style={{ fontSize: 13, color: 'var(--bln-text-muted)', lineHeight: 1.6, marginBottom: 18 }}>
              {installHelp === 'ios'
                ? 'iPhone Safari: toca el botón Compartir ⬆️ en la barra inferior y luego "Agregar a pantalla de inicio".'
                : 'Chrome / Edge: abre el menú ⋮ en la barra superior y selecciona "Instalar Bellason".'}
            </div>
            <button type="button"
              onClick={() => setInstallHelp(null)}
              style={{
                width: '100%', padding: 11, borderRadius: 10,
                background: 'var(--bln-accent)', color: 'var(--bln-text-on-acc)',
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
              }}
            >Entendido</button>
          </div>
        </div>
      )}

      {confirmLogout && (
        <div
          onClick={() => setConfirmLogout(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 110,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--bln-bg-elevated)', borderRadius: 16,
            padding: '20px 22px', maxWidth: 320, width: '100%',
            border: '1px solid var(--bln-border)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--bln-text)', marginBottom: 6 }}>
              ¿Cerrar sesión?
            </div>
            <div style={{ fontSize: 13, color: 'var(--bln-text-muted)', lineHeight: 1.5, marginBottom: 18 }}>
              Tendrás que volver a iniciar sesión para usar Bellason.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button"
                onClick={() => setConfirmLogout(false)}
                style={{
                  flex: 1, padding: 11, borderRadius: 10,
                  background: 'transparent', color: 'var(--bln-text-muted)',
                  border: '1px solid var(--bln-border)', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                }}
              >Cancelar</button>
              <button type="button"
                onClick={doLogout}
                style={{
                  flex: 1, padding: 11, borderRadius: 10,
                  background: 'var(--bln-danger)', color: 'var(--bln-text)',
                  border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 700,
                }}
              >Cerrar sesión</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Item({ icon, label, onClick, badge, danger, disabled }) {
  return (
    <button type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
        padding: '14px 16px', background: 'transparent',
        border: 'none', borderBottom: '1px solid var(--bln-border)',
        cursor: disabled ? 'default' : 'pointer',
        color: danger ? 'var(--bln-danger)' : 'var(--bln-text)',
        fontSize: 14, fontWeight: 600,
        textAlign: 'left', fontFamily: 'inherit',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge > 0 && (
        <span style={{
          background: 'var(--bln-danger)', color: 'var(--bln-text)',
          fontSize: 11, fontWeight: 700,
          minWidth: 20, height: 20, borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 6px',
        }}>{badge > 9 ? '9+' : badge}</span>
      )}
    </button>
  )
}

function NotifList({ notifs, onGo }) {
  if (notifs.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--bln-text-dim)', fontSize: 13 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
        Aún no tienes notificaciones
      </div>
    )
  }
  return (
    <div>
      {notifs.map((n) => (
        <button type="button"
          key={n.id}
          onClick={() => onGo(n.url)}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '12px 16px',
            borderBottom: '1px solid var(--bln-border)',
            background: 'transparent', border: 'none',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <div style={{ color: 'var(--bln-text)', fontSize: 14, fontWeight: 600 }}>{n.title}</div>
          <div style={{ color: 'var(--bln-text-muted)', fontSize: 12, marginTop: 2 }}>{n.body}</div>
          <div style={{ color: 'var(--bln-text-dim)', fontSize: 10, marginTop: 4 }}>
            {new Date(n.ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </button>
      ))}
    </div>
  )
}
